import * as React from 'react'
import { ParsedUrlQuery } from 'querystring'
import * as Types from 'declarations'
import { getDefaultImage } from 'lib/helper'
import { getCountryURL, getUploadThumbPath } from 'lib/display'
import * as RedisUtil from 'lib/redis'

import CountryPageTemplate from 'components/CountryPage'

import { withVersion } from '../../../lib/withVersion'

interface TrekPageProps {
	trips: Types.DB.Models.TripAttributes[]
	country: string
	version: Types.VERSION
	countryRecord?: Types.DB.Models.CountryAttributes
	countryISOCode: string
	guideCount: number
}

/**
 * Slim per-trip DTO keeping exactly the fields consumed by this page and
 * components/CountryPage (TrekCard, TripList, route map, gallery, SEO).
 * The full DB trip objects (17KB jsonData bodies, 30KB+ guideTrips with
 * nested guide companies) were bloating __NEXT_DATA__ to ~2.2MB.
 * Cast keeps the downstream component prop types unchanged — they only read
 * the fields mapped here.
 */
const countryPageTripDTO = (
	trip: Types.DB.Models.TripAttributes,
): Types.DB.Models.TripAttributes => {
	const highLevelInfo = trip.jsonData?.highLevelInfo

	return ({
		uniqueTripId: trip.uniqueTripId,
		name: trip.name,
		slug: trip.slug,
		isValidCompletePage: trip.isValidCompletePage,
		uploads: (trip.uploads ?? []).map((upload) => ({
			id: upload.id,
			dimensionsSmall: upload.dimensionsSmall,
			dimensionsLarge: upload.dimensionsLarge,
			uploadedBy: upload.uploadedBy,
			attributionText: upload.attributionText,
			attributionLink: upload.attributionLink,
			source: upload.source,
			user: upload.user
				? {
						guideCompany: upload.user.guideCompany
							? {
									id: upload.user.guideCompany.id,
									companyName: upload.user.guideCompany.companyName,
							  }
							: null,
				  }
				: null,
		})),
		// only `price` is read (guide cost summary + TrekCard lowest cost)
		guideTrips: (trip.guideTrips ?? []).map((guideTrip) => ({
			price: guideTrip.price,
		})),
		jsonData: {
			description: trip.jsonData?.description,
			trekOrClimb: trip.jsonData?.trekOrClimb,
			continent: trip.jsonData?.continent,
			countries: trip.jsonData?.countries,
			climbDifficulty: trip.jsonData?.climbDifficulty,
			trekDifficulty: trip.jsonData?.trekDifficulty
				? { average: trip.jsonData.trekDifficulty.average }
				: undefined,
			highLevelInfo: highLevelInfo
				? {
						climb: highLevelInfo.climb
							? { maxElevation: highLevelInfo.climb.maxElevation }
							: undefined,
						distances: highLevelInfo.distances
							? {
									total: highLevelInfo.distances.total
										? {
												distance: highLevelInfo.distances.total.distance,
												days: highLevelInfo.distances.total.days,
										  }
										: undefined,
							  }
							: undefined,
						climbRoute: highLevelInfo.climbRoute
							? {
									howManyDaysTotal: highLevelInfo.climbRoute.howManyDaysTotal,
							  }
							: undefined,
						whenToGo: highLevelInfo.whenToGo
							? { bestMonths: highLevelInfo.whenToGo.bestMonths }
							: undefined,
				  }
				: undefined,
			// only the first location is consumed (static route map)
			route: trip.jsonData?.route?.locations?.[0]
				? { locations: [trip.jsonData.route.locations[0]] }
				: undefined,
		},
	} as unknown) as Types.DB.Models.TripAttributes
}

const CountryPage = ({
	trips,
	country,
	version,
	countryRecord,
	countryISOCode,
	guideCount,
}: TrekPageProps) => {
	// todo: memoize all this?
	const uniqueBestMonths = new Set<number>()
	const allUploads: Types.DB.Models.UploadAttributes[] = []
	const locations: { latitude: number; longitude: number }[] = []

	trips.forEach((trip) => {
		const bestMonths = trip.jsonData?.highLevelInfo?.whenToGo?.bestMonths
		if (bestMonths) {
			bestMonths.forEach((month) => uniqueBestMonths.add(month))
		}
		if (trip.uploads) {
			// filter to non cover photos only
			allUploads.push(...trip.uploads.filter((upload, i) => i > 0))
		}

		const { latitude, longitude } = trip.jsonData.route?.locations?.[0] ?? {}
		if (!!latitude && !!longitude) {
			locations.push({ latitude, longitude })
		}
	})

	const uniqueBestMonthsSorted = Array.from(uniqueBestMonths).sort(
		(a, b) => a - b,
	)

	const images: {
		url: string
		width?: number
		height?: number
		alt?: string
		type: string
	}[] =
		(allUploads ?? []).length > 0
			? allUploads.map((image) => ({
					url: getUploadThumbPath(image.id, 'large'),
					width: +image.dimensionsLarge.split('x')[0],
					height: +image.dimensionsLarge.split('x')[1],
					alt: country,
					type: 'image/jpeg',
			  }))
			: [
					{
						url: getDefaultImage(version),
						type: 'image/jpeg',
					},
			  ]

	const capitalisedCountry = country.charAt(0).toUpperCase() + country.slice(1)

	const seo = {
		title: `Mountain climbing expeditions in ${capitalisedCountry} - Guided ${
			version === Types.VERSION.TREKKING ? 'Trekking' : 'Peaks'
		}`,
		description: `Explore ${
			version === Types.VERSION.TREKKING ? 'trekking' : 'mountaineering'
		} routes in ${capitalisedCountry} with our guide. Information for guided trips or solo adventures - routes, weather info, and more.`,

		canonical: getCountryURL(
			trips?.[0].jsonData.trekOrClimb,
			trips?.[0].jsonData.continent,
			country,
			version,
		),
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		images: images,
	}

	return (
		<CountryPageTemplate
			seo={seo}
			version={version}
			category={country}
			continent={trips?.[0].jsonData.continent}
			uniqueBestMonths={uniqueBestMonthsSorted}
			allUploads={allUploads.filter((_, i) => i < 35)}
			trips={trips}
			countryRecord={countryRecord}
			countryISOCode={countryISOCode}
			guideCount={guideCount}
		/>
	)
}

interface CountryPageQuery extends ParsedUrlQuery {
	routeType?: string
	countries?: string
}

// // for every possible page, generate a static version
// export const getStaticPaths = withVersion(
// 	async (context: { version: Types.VERSION }) => {
// 		const trips = await DataUtil.getHomePageTrips(context.version)

// 		const uniqueContinentCountryPairsValues = DataUtil.uniqueContinentCountryPairsFromTrips(
// 			trips,
// 		)

// 		const paths = uniqueContinentCountryPairsValues.map(
// 			({ continent, countryCode }) => {
// 				return {
// 					params: {
// 						routeType:
// 							context.version === Types.VERSION.TREKKING ? 'trek' : 'climbing',
// 						continent,
// 						countries: countryIsoCodeToSlugName(
// 							countryCode,
// 						).replaceAll(' ', '-'),
// 					},
// 				}
// 			},
// 		)
// 		return { paths, fallback: true }
// 	},
// )

export const getServerSideProps = withVersion(async (context) => {
	const { routeType, countries: country } = context.params as CountryPageQuery

	// todo: merge these two 404 checks in to one (route type and slug check), and maybe even the third - no trip found
	if (!(routeType === 'trek' || routeType === 'climbing')) {
		// 404?
		return {
			notFound: true,
		}
	}

	if (!country) {
		// 404
		return {
			notFound: true,
		}
	}

	const countryPageData = await RedisUtil.Data.CountryPages.SingleCountryPage.get(
		{
			version: context.version,
			slug: country,
		},
		// undefined,
		// true,
	)

	if (!countryPageData) {
		// 404
		return {
			notFound: true,
		}
	}

	const {
		trips: fullTrips,
		country: countrySlug,
		version,
		countryRecord,
		countryISOCode,
		guides,
	} = countryPageData

	if ((fullTrips ?? []).length < 1) {
		return {
			notFound: true,
		}
	}

	// slim the trips before they are serialised into __NEXT_DATA__
	const trips = (fullTrips ?? []).map(countryPageTripDTO)

	return {
		props: {
			trips,
			country: countrySlug,
			version,
			countryRecord,
			countryISOCode,
			// only the count is rendered on this page
			guideCount: guides?.length ?? 0,
		},
	}
})

export default CountryPage
